import { Command } from "commander";
import { readFileSync, writeFileSync, existsSync, mkdirSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import { confirm } from "@inquirer/prompts";
import { fatalError, warnAction } from "../ux/errors.js";
import {
  prepareContribution,
  previewContribution,
  exportContribution,
} from "@fathom/core";
import type { CalibratedProfile } from "@fathom/core";

/**
 * Load calibration data from the project's tracking/calibration files.
 * Looks for calibrated profiles in the .fathom/ directory.
 */
function loadCalibratedProfiles(): CalibratedProfile[] {
  const profiles: CalibratedProfile[] = [];

  // Try .fathom/tracking/summary.json + registry.json
  const registryPath = resolve(process.cwd(), ".fathom", "registry.json");
  const summaryPath = resolve(process.cwd(), ".fathom", "tracking", "summary.json");

  if (existsSync(registryPath) && existsSync(summaryPath)) {
    let registry: { features?: Array<{ id: string; name: string; estimated_tokens: number }> };
    try {
      registry = JSON.parse(readFileSync(registryPath, "utf-8"));
    } catch {
      fatalError(
        "Invalid registry file",
        `Failed to parse ${registryPath}`,
        "Delete the file and run `fathom analyze` to regenerate",
      );
    }
    let summary: { sessions?: Array<{ featureId?: string; status?: string; inputTokens?: number; outputTokens?: number; model?: string }> };
    try {
      summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
    } catch {
      fatalError(
        "Invalid tracking summary",
        `Failed to parse ${summaryPath}`,
        "Delete the file and run `fathom track` to regenerate",
      );
    }

    // Aggregate actuals per feature
    const actuals = new Map<string, { tokens: number; count: number; model: string }>();
    for (const session of summary.sessions ?? []) {
      if (!session.featureId || session.status === "untagged") continue;
      const existing = actuals.get(session.featureId) ?? {
        tokens: 0,
        count: 0,
        model: session.model ?? "unknown",
      };
      existing.tokens += (session.inputTokens ?? 0) + (session.outputTokens ?? 0);
      existing.count += 1;
      if (session.model) existing.model = session.model;
      actuals.set(session.featureId, existing);
    }

    for (const feature of registry.features ?? []) {
      const actual = actuals.get(feature.id);
      if (!actual) continue;

      profiles.push({
        taskType: feature.id,
        complexity: "M", // Default — registry doesn't store complexity
        model: actual.model,
        provider: "anthropic",
        estimatedTokens: feature.estimated_tokens,
        actualTokens: actual.tokens,
        overheadMultipliers: {
          contextResend: 0.7,
          toolCalls: 0.15,
          thinking: 1.5,
          errorRecovery: 0.15,
        },
        dataPoints: actual.count,
      });
    }
  }

  return profiles;
}

export const contributeCommand = new Command("contribute")
  .description("Share anonymized calibration data to improve Fathom for everyone")
  .option("--dry-run", "Preview what would be shared without saving")
  .option("--yes", "Skip confirmation prompt")
  .action(async (options: { dryRun?: boolean; yes?: boolean }) => {
    console.log(chalk.bold("\nFathom — Contribute Calibration Data"));
    console.log(chalk.dim("─".repeat(55)));
    console.log();
    console.log(
      "Sharing anonymized calibration data helps improve default",
    );
    console.log("estimates for all Fathom users.");

    // Load calibrated profiles
    const calibratedProfiles = loadCalibratedProfiles();

    if (calibratedProfiles.length === 0) {
      warnAction(
        "No calibration data found",
        "Cannot contribute without tracked sessions",
        "Run `fathom track` and `fathom calibrate` first",
      );
      return;
    }

    // Prepare anonymized data points
    const dataPoints = prepareContribution(calibratedProfiles);

    if (dataPoints.length === 0) {
      console.log(
        chalk.yellow(
          "\nNo valid calibration data points to share.",
        ),
      );
      return;
    }

    // Show preview
    console.log(chalk.bold("\nHere's what will be shared:\n"));
    console.log(previewContribution(dataPoints));
    console.log();
    console.log(
      chalk.dim(
        "What is NEVER shared: code, prompts, project names, API keys, or any identifying information.",
      ),
    );

    if (options.dryRun) {
      console.log(chalk.dim("\n  (Dry run — nothing saved)"));
      return;
    }

    // Confirm with user
    if (!options.yes) {
      try {
        const confirmed = await confirm({
          message: "Share this data?",
          default: false,
        });

        if (!confirmed) {
          console.log(chalk.dim("\n  Cancelled. No data was shared."));
          return;
        }
      } catch {
        // Handle Ctrl+C gracefully
        console.log(chalk.dim("\n  Cancelled."));
        return;
      }
    }

    // Save contribution locally
    const contributionsDir = resolve(process.cwd(), ".fathom", "contributions");
    mkdirSync(contributionsDir, { recursive: true });

    const today = new Date().toISOString().split("T")[0];
    const outputPath = resolve(contributionsDir, `${today}.json`);
    const json = exportContribution(dataPoints);
    writeFileSync(outputPath, json, "utf-8");

    console.log(chalk.green(`\n  ✓ Contribution saved to .fathom/contributions/${today}.json`));
    console.log(chalk.green("  ✓ Thank you! This helps make Fathom better for everyone."));
    console.log();
  });
